import React, { useEffect, useState } from 'react';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import machineSheetText from 'components/layout_and_quote_builder/utils/machineSheetText.js';
import { maxMachineDimensionOf, partLabelsByMachineId } from 'components/frank/utils/manufacturingLineListRendering.jsx';
import { fetchSectionAnswer } from 'components/frank/utils/sectionAnswerPolling.js';
import { restoredSectionListingUrl } from 'components/frank/utils/sectionListingUrl.js';

// Two rows: enough to give the owner an idea of what stops their manufacturing lines without listing every
// combination of their machines.
const ROWS_SHOWN = 2;

// The sets already recorded as shown on this page. The empty state remounts on every re-search, tab switch and
// wizard reopen, so a per-mount record would count one sighting many times over.
const recordedShownSets = new Set();

const sizeText = (size) => (size ? machineSheetText(size.width_mm, size.height_mm) : '');

export const disabledReasonText = (reason) => t(`quote_builder.frank_disabled_reason_${reason.key}`, {
  machine: reason.machine,
  thickness: reason.thickness_mm,
  material_sheet: sizeText(reason.material_sheet),
  maximum_sheet: sizeText(reason.maximum_sheet),
  minimum_sheet: sizeText(reason.minimum_sheet)
});

// The sub_sections a manufacturing line failed in, badged on each of its machines -- the listing's own per-machine
// badges, read off one line per label.
const partLabelsOf = (manufacturingLine) => partLabelsByMachineId({
  manufacturing_lines: (manufacturingLine.sub_section_labels || []).map((label) => ({
    ...manufacturingLine, sub_section_label: label
  }))
});

// Shown in the empty listing on the owner's OWN manufacturing lines: the manufacturing lines their machines could
// form, disabled, each with the first reason it is not listed. The server sends them closest-to-listed first
// (Frank::DisabledManufacturingLines), so the rows shown are the ones a small change would bring back.
//
// Tracked, to learn whether owners use it: once per page when a set is shown, and on every click on a card --
// which does nothing else, so a click is the owner trying to act on that manufacturing line.
//   separated draws an <hr /> above the section, for a placement where something else sits right above it (the
//   add-a-machine picker). Drawn here, so it only ever shows with the cards it separates.
const DisabledManufacturingLines = ({ disabledManufacturingLines, cardsPerRow, navSectionIndex, inWizard, separated }) => {
  const shown = (disabledManufacturingLines || []).slice(0, ROWS_SHOWN * cardsPerRow);
  const shownKey = shown.map((manufacturingLine) => manufacturingLine.identifier).join('|');

  useEffect(() => {
    if (shown.length === 0) { return; }
    const recordKey = `${window.user_quote_id}:${navSectionIndex}:${shownKey}`;
    if (recordedShownSets.has(recordKey)) { return; }
    recordedShownSets.add(recordKey);

    trackEvent('frank_disabled_manufacturing_lines_shown', {
      shown_count: shown.length,
      reason_keys: shown.map((manufacturingLine) => manufacturingLine.disabled_reason.key),
      nav_section_index: navSectionIndex,
      in_wizard: Boolean(inWizard),
      user_quote_id: window.user_quote_id
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shownKey names the set shown; one event per set.
  }, [shownKey]);

  if (shown.length === 0) { return null; }

  const maximumMachineDimensionMm = maxMachineDimensionOf([{ manufacturing_lines: shown }]);
  const trackClick = (manufacturingLine, position) => {
    trackEvent('frank_disabled_manufacturing_line_clicked', {
      manufacturing_line_identifier: manufacturingLine.identifier,
      reason_key: manufacturingLine.disabled_reason.key,
      position: position,
      nav_section_index: navSectionIndex,
      in_wizard: Boolean(inWizard),
      user_quote_id: window.user_quote_id
    });
  };

  return (
    <div data-disabled-manufacturing-lines>
      {separated && <hr />}
      <div style={{ textAlign: 'center', color: 'var(--ink, #10141a)', fontSize: '14px', fontWeight: 600, margin: '8px 0' }}>
        {t('quote_builder.frank_disabled_manufacturing_lines_heading')}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        justifyContent: shown.length < cardsPerRow ? 'center' : 'flex-start', alignItems: 'stretch'
      }}>
        {shown.map((manufacturingLine, position) => (
          <div
            key={manufacturingLine.identifier}
            data-disabled-manufacturing-line={manufacturingLine.identifier}
            aria-disabled="true"
            onClick={() => trackClick(manufacturingLine, position)}
            style={{
              display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
              flex: `0 0 calc(${100 / cardsPerRow}% - 16px)`, maxWidth: `calc(${100 / cardsPerRow}% - 16px)`,
              margin: '8px', padding: '12px 8px', cursor: 'not-allowed',
              border: '1px dashed #c4ccd6', borderRadius: '6px', background: '#f6f7f9'
            }}
          >
            <div style={{ opacity: 0.55 }}>
              <ManufacturingLineCard
                manufacturingLine={manufacturingLine}
                maxMachineDimensionMm={maximumMachineDimensionMm}
                partLabelsByMachineId={partLabelsOf(manufacturingLine)}
                bare
                hidePricedSolutionCount
                widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
              />
            </div>
            <div
              data-disabled-manufacturing-line-reason={manufacturingLine.disabled_reason.key}
              style={{ marginTop: 'auto', paddingTop: '10px', fontSize: '12px', textAlign: 'center', color: 'var(--danger, #c00)' }}
            >
              {disabledReasonText(manufacturingLine.disabled_reason)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// The guided setup wizard's add-a-machine step is drawn by the wizard itself, which asks for no listing -- so this
// asks for the section's, with the request its Manufacturing Line stage will send (restoredSectionListingUrl), and
// draws the disabled manufacturing lines it carries under the picker, as the page's empty state does.
//
// Asked straight through fetchSectionAnswer, NOT the response cache: the step ends with a machine added, which clears
// that cache, and an answer still in flight would land in it afterwards -- the Manufacturing Line stage that follows
// would then read the listing from BEFORE the machine as its own. Aborted when the step unmounts. Nothing is drawn
// while it is asked or when it fails: the picker above is the step.
export const SectionDisabledManufacturingLines = ({ navSectionIndex, count, cardsPerRow, inWizard }) => {
  const [disabledManufacturingLines, setDisabledManufacturingLines] = useState([]);

  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return undefined; }

    const controller = new AbortController();
    fetchSectionAnswer(restoredSectionListingUrl(navSectionIndex, count), { signal: controller.signal })
      .then((listing) => {
        if (controller.signal.aborted) { return; }
        setDisabledManufacturingLines(listing.disabled_manufacturing_lines);
      })
      .catch(() => {});
    return () => { controller.abort(); };
  }, [navSectionIndex, count]);

  return (
    <DisabledManufacturingLines
      disabledManufacturingLines={disabledManufacturingLines}
      cardsPerRow={cardsPerRow}
      navSectionIndex={navSectionIndex}
      inWizard={inWizard}
      separated
    />
  );
};

export default DisabledManufacturingLines;
